import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, FileText, Database, Globe, Server, AlertTriangle, Clock, KeyRound, UserCheck, Network, GitBranch, FileDigit, Fingerprint, Mail, Phone, ChevronRight } from 'lucide-react';

const SecurityPolicy = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero section */}
      <div className="relative bg-gradient-to-b from-indigo-900 to-purple-900 py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Security Policy</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              How we protect your data and ensure the security of our platform
            </p>
            <div className="flex justify-center mt-6">
              <div className="inline-flex items-center gap-2 text-sm text-indigo-300 bg-indigo-500/10 rounded-full px-4 py-1 border border-indigo-500/20">
                <Clock className="w-4 h-4" />
                Last Updated: {new Date().toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md">
          <div className="p-6 md:p-8 lg:p-10">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="prose prose-lg max-w-none text-gray-700"
            >
              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                  <Shield className="w-6 h-6 text-indigo-600" />
                  1. Our Security Commitment
                </h2>
                <p>
                  At LoadLink, security is not just a feature—it's a foundational principle built into everything we do. We're committed to protecting your data with the highest standards of security and compliance in the industry. Our comprehensive security program is designed to safeguard your information against unauthorized access, disclosure, alteration, and destruction.
                </p>
                <p>
                  This Security Policy outlines the measures we've implemented to protect your data and ensure the integrity of our platform. We continuously review and improve these measures to adapt to evolving threats and technologies.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Fingerprint className="w-6 h-6 text-indigo-600" />
                  2. Data Protection
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Encryption</h3>
                <p>We protect your data through:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>TLS/SSL encryption for all data in transit</li>
                  <li>AES-256 encryption for sensitive data at rest</li>
                  <li>End-to-end encryption for messaging and financial information</li>
                  <li>Secure key management with regular rotation</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Data Storage</h3>
                <p>Our approach to secure data storage includes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Geo-redundant data centers with multiple security layers</li>
                  <li>Regular data backups with secure off-site storage</li>
                  <li>Strict data isolation between customer environments</li>
                  <li>Data retention policies that limit storage to necessary timeframes</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 Data Minimization</h3>
                <p>
                  We adhere to the principle of data minimization, collecting and storing only the information necessary to provide our services. This reduces risk exposure and aligns with global privacy regulations.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Network className="w-6 h-6 text-indigo-600" />
                  3. Network Security
                </h2>
                <p>Our multi-layered network security includes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Enterprise-grade firewalls and intrusion detection systems</li>
                  <li>DDoS protection to ensure platform availability</li>
                  <li>Regular network vulnerability scanning</li>
                  <li>Network segmentation to isolate critical systems</li>
                  <li>24/7 monitoring for suspicious activities</li>
                  <li>Automated threat intelligence and response</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <KeyRound className="w-6 h-6 text-indigo-600" />
                  4. Access Controls
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Authentication</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Mandatory multi-factor authentication for all accounts</li>
                  <li>Strong password requirements with complexity enforcement</li>
                  <li>Session timeout and automatic lockout policies</li>
                  <li>Single sign-on (SSO) integration options</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Authorization</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Role-based access controls (RBAC) with principle of least privilege</li>
                  <li>Granular permission settings for data and feature access</li>
                  <li>Regular access reviews and privilege audits</li>
                  <li>Just-in-time access for administrative functions</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.3 Employee Access</h3>
                <p>
                  LoadLink employees only access customer data when necessary for support, maintenance, or to address security issues. All employee access is logged, audited, and requires approval through our access management system.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <UserCheck className="w-6 h-6 text-indigo-600" />
                  5. Secure Development Practices
                </h2>
                <p>Our software development lifecycle incorporates security at every stage:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Security requirements integrated into design specifications</li>
                  <li>Regular code reviews with security focus</li>
                  <li>Static and dynamic application security testing</li>
                  <li>Third-party dependency vulnerability scanning</li>
                  <li>Regular penetration testing by independent security firms</li>
                  <li>Security training for all development team members</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Server className="w-6 h-6 text-indigo-600" />
                  6. Infrastructure Security
                </h2>
                <p>
                  Our infrastructure is built on top-tier cloud providers with comprehensive security controls:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Infrastructure-as-code with security baseline enforcement</li>
                  <li>Immutable infrastructure with automated deployments</li>
                  <li>Container security with image scanning and runtime protection</li>
                  <li>Regular patching and vulnerability management</li>
                  <li>Hardened server configurations based on industry standards</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <FileDigit className="w-6 h-6 text-indigo-600" />
                  7. Compliance and Certifications
                </h2>
                <p>
                  LoadLink maintains compliance with industry standards and best practices, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>SOC 2 Type II certification</li>
                  <li>GDPR compliance</li>
                  <li>CCPA compliance</li>
                  <li>PCI DSS compliance for payment processing</li>
                  <li>Regular third-party security assessments</li>
                </ul>
                <p className="mt-4">
                  Compliance documentation is available to customers upon request under NDA.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <GitBranch className="w-6 h-6 text-indigo-600" />
                  8. Incident Response
                </h2>
                <p>
                  Our incident response program ensures rapid detection and effective management of security incidents:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Documented incident response procedures</li>
                  <li>Dedicated security incident response team</li>
                  <li>Automated alerts for suspicious activities</li>
                  <li>Regular incident response drills and tabletop exercises</li>
                  <li>Transparent breach notification process</li>
                </ul>
                <p className="mt-4">
                  In the event of a security incident affecting your data, we will notify you promptly in accordance with applicable laws and regulations.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <AlertTriangle className="w-6 h-6 text-indigo-600" />
                  9. Vulnerability Management
                </h2>
                <p>
                  We maintain a comprehensive vulnerability management program:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Regular vulnerability scanning of our entire infrastructure</li>
                  <li>Bug bounty program with security researchers</li>
                  <li>Structured vulnerability disclosure policy</li>
                  <li>Risk-based approach to vulnerability remediation</li>
                  <li>Regular security updates and patches</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Globe className="w-6 h-6 text-indigo-600" />
                  10. Physical Security
                </h2>
                <p>
                  Our physical security measures protect our infrastructure and operations:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Data centers with 24/7 security personnel</li>
                  <li>Multi-factor authentication for physical access</li>
                  <li>Video surveillance and intrusion detection systems</li>
                  <li>Environmental controls and monitoring</li>
                  <li>Secure asset management and decommissioning</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Mail className="w-6 h-6 text-indigo-600" />
                  11. Contact Information
                </h2>
                <p>If you have security concerns or want to report a vulnerability, please contact us at:</p>
                <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p><strong>Email:</strong> <a href="mailto:joe@loadlinkco.com" className="text-indigo-600 hover:text-indigo-800">joe@loadlinkco.com</a></p>
                  <p className="mt-2"><strong>For sensitive security reports:</strong> Use our <a href="#" className="text-indigo-600 hover:text-indigo-800">encrypted reporting form</a></p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Related links section */}
        <div className="max-w-4xl mx-auto mt-10">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Related Documents</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link 
              to="/privacy-policy"
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-indigo-600 mr-3" />
                <span>Privacy Policy</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            <Link 
              to="/terms-of-service"
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center">
                <Lock className="w-5 h-5 text-indigo-600 mr-3" />
                <span>Terms of Service</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
          </div>
        </div>

        {/* Security contact section */}
        <div className="max-w-4xl mx-auto mt-10 bg-indigo-50 rounded-xl p-6 border border-indigo-100">
          <div className="flex flex-col sm:flex-row items-center">
            <div className="mb-6 sm:mb-0 sm:mr-6">
              <div className="bg-indigo-100 p-4 rounded-full">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Security is our highest priority</h3>
              <p className="text-gray-600 mb-4">Our security team works around the clock to protect your data and privacy.</p>
              <a 
                href="mailto:joe@loadlinkco.com" 
                className="inline-flex items-center bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Contact Security Team
                <ChevronRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityPolicy;